import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useClipboard from 'react-use-clipboard';
import moment from 'moment';

import Alert from '../Alert';
import Button from '../Button';
import CheckmarkIcon from '../../icons/CheckmarkIcon';
import useAuthenticated from '../../../hooks/useAuthenticated';

const propTypes = {
  promoCode: PropTypes.shape({
    code: PropTypes.string.isRequired,
    expires_at: PropTypes.string,
  }),
  autoApplied: PropTypes.bool,
  className: PropTypes.string,
};

// Tells the consumer their Digital Photo Pass is free with the given promo
// code. Renders nothing without a code or for unauthenticated viewers (the
// code's eligibility is only meaningful for a logged-in account).
const FreeSubscriptionPromoBanner = ({
  promoCode,
  autoApplied = false,
  className,
}) => {
  const authenticated = useAuthenticated();
  const [copied, setCopied] = useClipboard(promoCode?.code ?? '', {
    successDuration: 2000,
  });

  if (!authenticated || !promoCode?.code) {
    return null;
  }

  const classes = classNames({
    'free-subscription-promo-banner': true,
    [className]: !!className,
  });

  return (
    <Alert
      type="info"
      className={classes}
      borderless
      data-testid="free-subscription-promo-banner"
    >
      <div className="free-subscription-promo-banner-body">
        <p className="mb-0">
          Your Digital Photo Pass is{' '}
          <span className="text-primary text-extra-bold">free</span> with promo
          code{' '}
          <span
            className="text-primary text-extra-bold"
            data-testid="free-subscription-promo-banner-code"
          >
            {promoCode.code}
          </span>
          .
          {autoApplied && (
            <Fragment> It will be applied automatically at checkout.</Fragment>
          )}
        </p>
        {promoCode.expires_at && (
          <p className="text-sm mb-0">
            Offer ends{' '}
            <span className="text-extra-bold">
              {moment.utc(promoCode.expires_at).format('MMMM D, YYYY')}
            </span>
            .
          </p>
        )}
      </div>

      <Button
        pill
        btnOutline
        btnSize="sm"
        className="free-subscription-promo-banner-copy"
        onClick={setCopied}
        data-testid="free-subscription-promo-banner-copy"
      >
        {copied ? (
          <Fragment>
            <span className="free-subscription-promo-banner-copy-check">
              <CheckmarkIcon size={12} color="white" />
            </span>
            Copied
          </Fragment>
        ) : (
          'Copy code'
        )}
      </Button>
    </Alert>
  );
};

FreeSubscriptionPromoBanner.propTypes = propTypes;

export default FreeSubscriptionPromoBanner;
